import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { ConfigProvider, App as AntApp, Spin } from "antd";
import { AuthProvider, useAuth } from "./AuthContext";
import AdminLayout from "./AdminLayout";
import Login from "./Login";
import Dashboard from "./pages/Dashboard";
import GroupDashboard from "./pages/GroupDashboard";
import UsersPage from "./pages/UsersPage";
import GroupsPage from "./pages/GroupsPage";
import MyTeamPage from "./pages/MyTeamPage";
import EventsPage from "./pages/EventsPage";
import NewsPage from "./pages/NewsPage";
import PortfolioPage from "./pages/PortfolioPage";
import GiftsPage from "./pages/GiftsPage";
import PostsPage from "./pages/PostsPage";
import GroupPosts from "./pages/GroupPosts";
import NotificationsPage from "./pages/NotificationsPage";
import CertificatesPage from "./pages/CertificatesPage";
import FeedbackPage from "./pages/FeedbackPage";
import OrganizationsPage from "./pages/OrganizationsPage";
import ReportsPage from "./pages/ReportsPage";
import MyGroupPage from "./pages/MyGroupPage";
import "./admin.css";

const adminTheme = {
    token: {
        colorPrimary: "#2563eb",
        borderRadius: 8,
        fontFamily:
            "Inter, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        fontSize: 14,
    },
};

// SUPER_ADMIN-only route guard.
const SuperOnly = ({ admin, children }) =>
    admin.role === "SUPER_ADMIN" ? children : <Navigate to="/admin/my-team" replace />;

const AdminGate = () => {
    const { admin, loading } = useAuth();

    if (loading) {
        return (
            <div className="admin-fullscreen admin-center">
                <Spin size="large" />
            </div>
        );
    }

    if (!admin) return <Login />;

    return (
        <AdminLayout>
            <Routes>
                <Route
                    index
                    element={
                        admin.role === "SUPER_ADMIN" ? (
                            <Dashboard />
                        ) : (
                            <GroupDashboard />
                        )
                    }
                />
                <Route
                    path="users"
                    element={
                        <SuperOnly admin={admin}>
                            <UsersPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="groups"
                    element={
                        <SuperOnly admin={admin}>
                            <GroupsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="notifications"
                    element={
                        <SuperOnly admin={admin}>
                            <NotificationsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="certificates"
                    element={
                        <SuperOnly admin={admin}>
                            <CertificatesPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="feedback"
                    element={
                        <SuperOnly admin={admin}>
                            <FeedbackPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="organizations"
                    element={
                        <SuperOnly admin={admin}>
                            <OrganizationsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="reports"
                    element={
                        <SuperOnly admin={admin}>
                            <ReportsPage />
                        </SuperOnly>
                    }
                />
                <Route path="my-group" element={<MyGroupPage />} />
                <Route path="my-team" element={<MyTeamPage />} />
                <Route
                    path="events"
                    element={
                        <SuperOnly admin={admin}>
                            <EventsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="news"
                    element={
                        <SuperOnly admin={admin}>
                            <NewsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="portfolio"
                    element={
                        <SuperOnly admin={admin}>
                            <PortfolioPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="gifts"
                    element={
                        <SuperOnly admin={admin}>
                            <GiftsPage />
                        </SuperOnly>
                    }
                />
                <Route
                    path="posts"
                    element={
                        admin.role === "SUPER_ADMIN" ? <PostsPage /> : <GroupPosts />
                    }
                />
                <Route path="*" element={<Navigate to="/admin" replace />} />
            </Routes>
        </AdminLayout>
    );
};

const AdminApp = () => (
    <ConfigProvider theme={adminTheme}>
        <AntApp className="admin-fullscreen">
            <AuthProvider>
                <AdminGate />
            </AuthProvider>
        </AntApp>
    </ConfigProvider>
);

export default AdminApp;
